import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import type { components } from '@/api/types.gen';

// Бэк-неймспейс — `/admin/users` и `/admin/invitations` (см. openapi-m1.yaml).
// Внутри UI оставляем «Сотрудники» (Employees) — это слово ближе к реалиям
// оптики; смысловой 1:1-перевод users.
export type User = components['schemas']['UserRead'];
export type UserPage = components['schemas']['UserPage'];
export type UserRole = components['schemas']['UserRole'];
export type Invitation = components['schemas']['InvitationRead'];
export type InvitationPage = components['schemas']['InvitationPage'];
export type InvitationCreate = components['schemas']['InvitationCreate'];
export type InvitationResponse = components['schemas']['InvitationResponse'];
export type UserResetLinkResponse = components['schemas']['UserResetLinkResponse'];

export function useUsersQuery(params: { page?: number; pageSize?: number } = {}) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 50;
  return useQuery({
    queryKey: ['users', { page, pageSize }],
    queryFn: () => api.get<UserPage>(`/api/v1/admin/users?page=${page}&page_size=${pageSize}`),
  });
}

export function useInvitationsQuery(params: { page?: number; pageSize?: number } = {}) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 50;
  return useQuery({
    queryKey: ['invitations', { page, pageSize }],
    queryFn: () =>
      api.get<InvitationPage>(`/api/v1/admin/invitations?page=${page}&page_size=${pageSize}`),
  });
}

export function useCreateInvitationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['invitations', 'create'],
    mutationFn: (input: InvitationCreate) =>
      api.post<InvitationResponse>('/api/v1/admin/invitations', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}

/**
 * Генерация ссылки сброса пароля сотруднику (M7.3-b2, admin-only).
 *
 * `POST /admin/users/{membership_id}/reset-link` → 201 `{reset_link}`. Письмо
 * НЕ шлётся: владелец копирует ссылку и передаёт её сотруднику сам (закрывает
 * email-less персонал). Симметрично invite_link. `membership_id` = `UserRead.id`
 * (это `staff_membership.id`). Ошибки: 404 (чужой/неактивный), 403 (employee).
 */
export function useResetLinkMutation() {
  return useMutation({
    mutationKey: ['users', 'reset-link'],
    mutationFn: (membershipId: string) =>
      api.post<UserResetLinkResponse>(`/api/v1/admin/users/${membershipId}/reset-link`),
  });
}

export function useRevokeInvitationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['invitations', 'revoke'],
    mutationFn: (invitationId: string) =>
      api.delete<void>(`/api/v1/admin/invitations/${invitationId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}
